import { useEffect, useState, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { Card, Color } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { api } from '../../lib/api-client.js';

// ── Color pip helpers ──────────────────────────────────────────────────────────

const COLOR_BG: Record<Color, string> = {
  W: 'bg-yellow-100 text-yellow-900',
  U: 'bg-blue-600 text-white',
  B: 'bg-gray-800 text-white border border-gray-600',
  R: 'bg-red-600 text-white',
  G: 'bg-green-600 text-white',
};

const COLOR_LABEL: Record<Color, string> = { W: 'W', U: 'U', B: 'B', R: 'R', G: 'G' };

function ColorPips({ colors }: { colors: Color[] }) {
  if (colors.length === 0) return <span className="text-[9px] text-gray-500">Colorless</span>;
  return (
    <div className="flex gap-0.5 items-center">
      {colors.map((c) => (
        <span
          key={c}
          className={`text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center ${COLOR_BG[c]}`}
        >
          {COLOR_LABEL[c]}
        </span>
      ))}
    </div>
  );
}

// ── Placeholder builder ────────────────────────────────────────────────────────

/**
 * Build a synthetic placeholder card for tokens that can't be found via search.
 */
function makePlaceholderToken(name: string, pt: string): Card {
  return {
    id: `placeholder-${Date.now()}`,
    oracleId: `placeholder-oracle-${Date.now()}`,
    name: name || 'Token',
    manaCost: '',
    cmc: 0,
    typeLine: 'Token Creature',
    oracleText: pt ? `${pt}` : '',
    colorIdentity: [],
    colors: [],
    keywords: [],
    layout: 'token',
    legalities: { commander: 'not_legal' },
    producedMana: [],
    imageUris: undefined,
    card_faces: undefined,
    all_parts: undefined,
    set: 'placeholder',
    setName: 'Placeholder',
    rarity: 'common',
    artist: '',
    collectorNumber: '0',
    releasedAt: '',
    isRebalanced: false,
    digital: false,
    prices: {},
  } as unknown as Card;
}

// ── TokenCard ─────────────────────────────────────────────────────────────────

/**
 * A single token entry: full card image + identifying info.
 * Draggable to player battlefield or horde battlefield.
 * Clicking also places on the player battlefield.
 */
interface TokenCardProps {
  card: Card;
  onClickPlace: (card: Card) => void;
}

function TokenCard({ card, onClickPlace }: TokenCardProps) {
  // Full card image (not art crop) — 'normal' gives the full frame with P/T
  const imageUri = getCardImageUri(card, 'normal');
  const pt = card.power !== undefined && card.toughness !== undefined
    ? `${card.power}/${card.toughness}`
    : null;

  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `token-panel-${card.id}`,
    data: { fromTokenPanel: true, card },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`relative flex flex-col items-center gap-1 rounded-lg border border-gray-700 hover:border-orange-500 transition cursor-grab active:cursor-grabbing bg-gray-800 hover:bg-gray-750 p-1.5 ${isDragging ? 'opacity-30' : ''}`}
      style={{ width: 120 }}
      onClick={() => onClickPlace(card)}
      title={`${card.name}${pt ? ` (${pt})` : ''} — drag to battlefield`}
    >
      {/* Full card image */}
      {imageUri ? (
        <img
          src={imageUri}
          alt={card.name}
          className="rounded w-full"
          style={{ aspectRatio: '488/680', objectFit: 'cover' }}
          draggable={false}
        />
      ) : (
        <div
          className="w-full rounded bg-gray-700 flex items-center justify-center"
          style={{ aspectRatio: '488/680' }}
        >
          <span className="text-gray-400 text-xs text-center px-1 leading-tight">{card.name}</span>
        </div>
      )}

      {/* Name */}
      <span className="text-[10px] text-gray-200 font-medium text-center leading-tight line-clamp-2 w-full">
        {card.name}
      </span>

      {/* P/T */}
      {pt && (
        <span className="text-[9px] text-orange-300 font-bold">{pt}</span>
      )}

      {/* Type line */}
      <span className="text-[8px] text-gray-500 text-center leading-tight truncate w-full">
        {card.typeLine}
      </span>

      {/* Colors */}
      <ColorPips colors={(card.colors ?? []) as Color[]} />
    </div>
  );
}

// ── AddTokenPanel ─────────────────────────────────────────────────────────────

export default function AddTokenPanel() {
  const placeToken = usePlaytesterStore((s) => s.placeToken);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const deckTokens = usePlaytesterStore((s) => s.deckTokens);
  const setDeckTokens = usePlaytesterStore((s) => s.setDeckTokens);
  const tokenSearchResults = usePlaytesterStore((s) => s.tokenSearchResults);
  const tokenSearchQuery = usePlaytesterStore((s) => s.tokenSearchQuery);
  const setTokenSearchResults = usePlaytesterStore((s) => s.setTokenSearchResults);
  const battlefield = usePlaytesterStore((s) => s.battlefield);
  const hand = usePlaytesterStore((s) => s.hand);
  const graveyard = usePlaytesterStore((s) => s.graveyard);
  const exile = usePlaytesterStore((s) => s.exile);
  const command = usePlaytesterStore((s) => s.command);

  const [isLoadingDeckTokens, setIsLoadingDeckTokens] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInput, setSearchInput] = useState(tokenSearchQuery);
  const [placeholderName, setPlaceholderName] = useState('');
  const [placeholderPt, setPlaceholderPt] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load deck tokens on mount if not already loaded
  useEffect(() => {
    if (deckTokens !== null) return;

    const allCards = [...battlefield, ...hand, ...graveyard, ...exile, ...command];
    const cardIds = [...new Set(allCards.map((i) => i.card.id))];
    if (cardIds.length === 0) {
      setDeckTokens([]);
      return;
    }

    setIsLoadingDeckTokens(true);
    api.deckTokens(cardIds)
      .then((result) => setDeckTokens(result.tokens))
      .catch(() => setDeckTokens([]))
      .finally(() => setIsLoadingDeckTokens(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced Scryfall search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!searchInput.trim()) {
      setTokenSearchResults([], '');
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const q = `t:token name:${searchInput}`;
        const result = await api.search(q);
        setTokenSearchResults(result.cards, searchInput);
      } catch {
        setTokenSearchResults([], searchInput);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [searchInput, setTokenSearchResults]);

  function handlePlaceToken(card: Card) {
    placeToken(card);
    setOpenPanel(null);
  }

  function handlePlaceholder() {
    if (!placeholderName.trim()) return;
    const token = makePlaceholderToken(placeholderName.trim(), placeholderPt.trim());
    placeToken(token);
    setOpenPanel(null);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setOpenPanel(null)}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[600px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-white">Add Token</h2>
            <p className="text-[10px] text-gray-500 mt-0.5">Drag to player or horde battlefield · click to place on player side</p>
          </div>
          <button onClick={() => setOpenPanel(null)} className="text-gray-500 hover:text-white transition text-lg leading-none">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Section 1: Deck Tokens */}
          <div className="px-4 pt-3 pb-2 border-b border-gray-800">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Deck Tokens
            </div>
            {isLoadingDeckTokens ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0" />
                Loading...
              </div>
            ) : !deckTokens || deckTokens.length === 0 ? (
              <div className="text-xs text-gray-600 py-2">
                {deckTokens === null ? 'Loading...' : 'No tokens found for this deck.'}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {deckTokens.map((card) => (
                  <TokenCard key={card.id} card={card} onClickPlace={handlePlaceToken} />
                ))}
              </div>
            )}
          </div>

          {/* Section 2: Scryfall Token Search */}
          <div className="px-4 pt-3 pb-2 border-b border-gray-800">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Search Tokens
            </div>
            <input
              type="text"
              placeholder="Token name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
              autoFocus
            />
            {isSearching && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                <div className="w-3 h-3 border border-orange-500 border-t-transparent rounded-full animate-spin" />
                Searching...
              </div>
            )}
            {!isSearching && tokenSearchResults.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tokenSearchResults.slice(0, 20).map((card) => (
                  <TokenCard key={card.id} card={card} onClickPlace={handlePlaceToken} />
                ))}
              </div>
            )}
            {!isSearching && searchInput.trim() && tokenSearchResults.length === 0 && (
              <div className="text-xs text-gray-600 mt-2">No results</div>
            )}
          </div>

          {/* Section 3: Manual Placeholder */}
          <div className="px-4 pt-3 pb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Manual Placeholder
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Token name"
                value={placeholderName}
                onChange={(e) => setPlaceholderName(e.target.value)}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
              />
              <input
                type="text"
                placeholder="P/T (e.g. 2/2)"
                value={placeholderPt}
                onChange={(e) => setPlaceholderPt(e.target.value)}
                className="w-24 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
              />
              <button
                onClick={handlePlaceholder}
                disabled={!placeholderName.trim()}
                className="px-3 py-1 text-sm font-medium bg-orange-600 hover:bg-orange-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
